import type { Messages } from '../messages';

/**
 * FR text for every reason code (C2).
 *
 * Provisional until Q3: "tu", to match the mockups, and a plain register (register inconsistency
 * #3: FXM-04 §7 asks for plain French, I12 requires FR and EN, so the same plain register is used
 * in both). Each text stands on its own because the bot sends it too (C2 spec R10).
 */
export const reasonFr: Messages['reason'] = {
  INTERNAL_ERROR: 'Un problème est survenu de notre côté. Réessaie dans un instant.',
  VALIDATION_FAILED:
    'Certaines informations sont manquantes ou incorrectes. Vérifie les champs signalés.',
  NOT_FOUND: 'Nous n’avons pas trouvé cet élément. Il a peut-être été supprimé.',
  UNAUTHENTICATED: 'Ta session a pris fin. Ferme FXMate et rouvre-le depuis Telegram.',
  TOKEN_EXPIRED: 'Ta session a expiré. Ferme FXMate et rouvre-le depuis Telegram.',
  INIT_DATA_INVALID:
    'Nous n’avons pas pu confirmer ton identité Telegram. Rouvre FXMate depuis Telegram.',
  INIT_DATA_EXPIRED:
    'FXMate a été ouvert il y a trop longtemps. Ferme-le et rouvre-le depuis Telegram.',
  INIT_DATA_MISSING: 'Ouvre FXMate depuis Telegram.',
  IDEMPOTENCY_KEY_REQUIRED: 'Ta demande n’a pas pu être traitée. Réessaie.',
  IDEMPOTENCY_KEY_REUSED: 'Cette demande a déjà été envoyée avec d’autres informations. Réessaie.',
  IDEMPOTENCY_KEY_IN_PROGRESS:
    'Ta demande précédente est encore en cours de traitement. Patiente un instant, puis vérifie le résultat.',
  RATE_LIMITED: 'Trop de tentatives en peu de temps. Patiente un moment, puis réessaie.',
  SERVICE_UNAVAILABLE: 'FXMate est momentanément indisponible. Réessaie dans quelques minutes.',

  TELEGRAM_PHONE_NUMBER_INVALID: 'Ce numéro ne correspond à aucun compte Telegram. Vérifie-le.',
  TELEGRAM_CODE_INVALID:
    'Ce code ne correspond pas. Vérifie le dernier message de Telegram et réessaie.',
  TELEGRAM_CODE_EXPIRED: 'Ce code a expiré. Demande un nouveau code.',
  TELEGRAM_CODE_RATE_LIMITED:
    'Telegram limite les demandes de code. Réessaie dans {{minutes}} min.',
  TELEGRAM_PASSWORD_REQUIRED:
    'Ton compte Telegram est protégé par la validation en deux étapes. Saisis ton mot de passe Telegram pour continuer.',
  TELEGRAM_PASSWORD_INVALID:
    'Ce mot de passe Telegram est incorrect. C’est celui que tu as choisi dans Telegram, pas le code à 5 chiffres.',
  TELEGRAM_LOGIN_NOT_STARTED: 'Ta connexion à Telegram a été interrompue. Demande un nouveau code.',
  TELEGRAM_ACCOUNT_MISMATCH:
    'Ce compte Telegram n’est pas celui avec lequel tu as ouvert FXMate. Utilise le même compte.',
  TELEGRAM_UNAVAILABLE: 'Telegram ne répond pas. Réessaie dans un instant.',
  TELEGRAM_SESSION_LOST:
    'FXMate n’est plus connecté à ton compte Telegram, peut-être après une déconnexion sur un autre appareil. La copie est suspendue. Reconnecte-toi pour la reprendre.',

  CHANNEL_INACCESSIBLE:
    'Ce canal n’est plus disponible : tu l’as quitté ou il a été supprimé. Sa copie est arrêtée.',
  SUBSCRIPTION_ALREADY_EXISTS: 'Tu copies déjà ce canal.',
  CHANNEL_NOT_CONFIGURED: 'Ce canal ne copie rien tant qu’il n’est pas réglé.',
  CHANNEL_PAUSED:
    'Ce canal est en pause. Les nouveaux signaux sont ignorés, les positions ouvertes restent suivies.',
  CHANNEL_UNREACHABLE:
    'FXMate n’arrive plus à lire ce canal depuis plusieurs minutes. Ses signaux peuvent être copiés en retard ou manqués tant que la lecture n’a pas repris.',
  CONFIRMATION_UNANSWERED:
    'Tu n’as répondu à aucun signal de ce canal depuis 2 semaines. Tu peux le passer en mode automatique dans ses réglages ; rien ne change si tu restes en confirmation.',

  TRADING_ACCOUNT_CREDENTIALS_REJECTED:
    'Ton courtier a refusé le numéro de compte ou le mot de passe. Vérifie-les et réessaie.',
  TRADING_ACCOUNT_SERVER_NOT_FOUND:
    'Nous n’avons pas pu joindre ce serveur. Vérifie le nom du serveur sur l’écran de connexion de ton application MetaTrader.',
  TRADING_ACCOUNT_READ_ONLY:
    'Ce mot de passe permet seulement de consulter le compte, pas de passer des ordres. Utilise ton mot de passe principal, celui qui permet de trader dans MetaTrader.',
  TRADING_ACCOUNT_LINKED_TO_OTHER_TRADER:
    'Ce compte de trading est déjà relié à un autre utilisateur FXMate.',
  // P1 (Q264, provisional): netting or exchange mode. An unreadable mode is a 503, not this code.
  TRADING_ACCOUNT_NETTING_NOT_SUPPORTED:
    'FXMate copie seulement sur un compte MT5 en mode « hedging », où chaque trade reste une position à part. Ton compte est en mode « netting » : tes trades et ceux de FXMate y seraient fusionnés en une seule position. Demande à ton courtier un compte en mode hedging, puis relie-le ici.',
  TRADING_ACCOUNT_BALANCE_LOW:
    'Ton solde est inférieur au plus petit ordre accepté par ton courtier. En l’état, aucun trade ne peut être copié.',
  TRADING_ACCOUNT_UNREACHABLE:
    'Ton compte de trading ne répond pas. Ton mot de passe a peut-être changé chez ton courtier. La copie est suspendue jusqu’à la reconnexion du compte.',
  TRADING_ACCOUNT_ALREADY_CONNECTED:
    'Tu as déjà un compte connecté. Déconnecte-le depuis Compte pour en relier un autre.',
  TRADING_ACCOUNT_NOT_CONNECTED:
    'Aucun compte de trading n’est encore connecté. Connecte d’abord ton compte MT5.',
  CONSENT_VERSION_UNKNOWN:
    'Le texte d’autorisation a changé. Lis la nouvelle version et confirme à nouveau.',

  SETTINGS_FALLBACK_SL_REQUIRED:
    'Saisis une distance de stop loss de secours pour enregistrer ces réglages.',
  SETTINGS_VERSION_CONFLICT:
    'Ces réglages ont changé sur un autre appareil. Recharge pour voir la dernière version.',
  RISK_PREVIEW_UNAVAILABLE:
    'Impossible de calculer ton risque pour l’instant. Enregistrement indisponible.',
  DAILY_LOSS_LIMIT_APPROACHED: 'Tu as atteint la moitié de ta perte maximale du jour.',
  DAILY_LOSS_LIMIT_REACHED:
    'Ta perte maximale du jour est atteinte. Aucune nouvelle position ne s’ouvre avant minuit (heure de Paris). Les clôtures et déplacements de stop loss continuent.',
  DAILY_LOSS_LIMIT_ALREADY_EXCEEDED:
    'Ta perte du jour dépasse déjà cette limite : aucune nouvelle ouverture jusqu’à minuit (heure de Paris). Les clôtures continuent.',
  MAX_POSITIONS_REACHED:
    'Le nombre maximal de positions ouvertes pour ce canal est atteint. Les nouveaux signaux ne sont pas copiés tant qu’une position n’est pas fermée.',

  SIGNAL_WITHOUT_STOP_LOSS:
    'Ce signal n’a pas de stop loss et tes réglages ignorent ce type de signal. Il n’a pas été copié.',
  CONFIRMATION_EXPIRED: 'Tu n’as pas confirmé ce signal à temps. Le trade n’a pas été pris.',
  CONFIRMATION_CANCELLED: 'Tu as mis ce canal en pause : le signal en attente a été annulé.',
  CONFIRMATION_OBSOLETE:
    'Le canal a annulé ou clôturé ce trade avant ta réponse. Rien n’a été fait.',
  CONFIRMATION_DECLINED: 'Tu as ignoré ce signal. Rien n’a été fait.',

  EMERGENCY_PAUSE_FAILED: 'Ce canal n’a pas pu être mis en pause. Réessaie.',
  POSITIONS_UNAVAILABLE:
    'Impossible d’afficher tes positions : la clôture n’est pas disponible. La mise en pause reste possible.',
  POSITION_CLOSE_FAILED:
    'Ton courtier n’a pas encore clôturé cette position. FXMate réessaie automatiquement.',

  INSTRUMENT_NOT_FOUND:
    'Cet instrument n’est pas disponible sur ton compte de trading. Le trade n’a pas été pris.',
  INSTRUMENT_UNAVAILABLE:
    'Ton courtier ne permet pas de trader cet instrument pour le moment. Le trade n’a pas été pris.',
  MARKET_CLOSED:
    'Le marché de cet instrument est fermé. Le trade n’a pas été pris et ne sera pas placé plus tard.',
  SIZE_BELOW_MINIMUM:
    'Avec tes réglages, la position serait plus petite que le minimum de ton courtier. Le trade n’a pas été pris. Augmente ton risque ou passe en lot fixe.',
  SIZE_ABOVE_MAXIMUM:
    'Avec tes réglages, la position serait plus grande que ce qu’autorise ton courtier. Le trade n’a pas été pris : FXMate ne réduit jamais une taille de lui-même.',
  // W4 R7, Q161 (provisional): a fixed lot off the broker's step is refused, never rounded (I7).
  SIZE_NOT_ON_STEP:
    'Ton lot fixe n’est pas un multiple du pas de ton courtier. Le trade n’a pas été pris : FXMate n’arrondit jamais une taille de lui-même. Choisis un lot que ton courtier accepte.',
  // W4 R9, Q162 (provisional): no order without the stop loss the channel gave.
  STOP_LOSS_ALREADY_PASSED:
    'Le prix avait déjà dépassé le stop loss du signal. Le trade n’a pas été pris et ne sera pas placé plus tard.',
  INSUFFICIENT_MARGIN:
    'La marge disponible sur ton compte est insuffisante pour ouvrir cette position. Le trade n’a pas été pris.',
  ORDER_REJECTED:
    'Ton courtier a refusé l’ordre, même après plusieurs tentatives pendant 30 secondes. Le trade n’a pas été pris.',
  TRADING_ACCOUNT_SERVER_NOT_ALLOWED:
    'Pour l’instant, FXMate ne passe d’ordres que sur des comptes de démo. Ce compte n’est pas sur un serveur de démo autorisé : aucun ordre n’a été passé.',
  // W7 R36 (c), C2 task 11 (Proposal, to be validated; Q242): the ACCOUNT_PROBLEM notice only.
  TRADING_ACCOUNT_NOT_RESPONDING:
    'Ton compte de trading ne répond plus depuis quelques minutes. FXMate ne peut rien exécuter sur ton compte tant qu’il ne répond pas ; la copie reprendra toute seule. Si ça dure, vérifie la connexion dans l’onglet Compte.',
  MODIFICATION_REFUSED_BY_BROKER:
    'Ton courtier a refusé le nouveau niveau demandé. Rien n’a été modifié : la position garde son stop loss et son take profit actuels.',
  STOP_LOSS_REFUSED_BY_BROKER:
    'Ton courtier a accepté l’ordre mais refusé son stop loss, trop proche du prix. La position est ouverte sans stop loss : surveille-la.',
  PARTIAL_CLOSE_ROUNDED_TO_FULL:
    'La clôture partielle demandée par le canal était inférieure à la taille minimale de ton courtier : la position a été clôturée en totalité.',
  ACCOUNT_LIQUIDATED:
    'Ton courtier a liquidé ton compte, l’ordre n’a donc pas pu passer. La copie est suspendue sur ce compte.',
  // W7 §4.3 item 7, Q202 (Proposal, to be validated): true for an unreachable account (FXM-04 §5)
  // and for an opening older than three minutes (FXM-03 §6 case 10, W6 R6).
  INSTRUCTION_MISSED:
    'FXMate n’a pas pu appliquer cette instruction au moment où le canal l’a publiée : elle a été ignorée. Les trades manqués ne sont pas rattrapés.',
  CLOSE_DELAYED:
    'Ton compte de trading était injoignable quand le canal a demandé la clôture de cette position. Elle a été clôturée dès le retour du compte.',
  PRICE_GAP:
    'Ton courtier a exécuté l’ordre à un prix nettement différent de celui annoncé par le canal. La position est conservée : vérifie-la dans Positions.',

  POSITION_TRACKING_LOST:
    'Ton compte de trading est injoignable : FXMate ne connaît plus l’état réel de cette position.',
  POSITION_MISMATCH:
    'Cette position ne correspond pas à ce que FXMate attendait sur ton compte. FXMate ne l’a pas modifiée. Vérifie-la dans MetaTrader.',
  TIMELINE_UNAVAILABLE: 'Les messages du canal ne sont pas disponibles pour cette position.',

  CLOSED_BY_CHANNEL: 'Clôturée par le canal',
  CLOSED_BY_STOP_LOSS: 'Stop loss atteint',
  CLOSED_BY_TAKE_PROFIT: 'Take profit atteint',
  CLOSED_BY_TRADER: 'Clôturée manuellement dans MetaTrader',
  CLOSED_BY_EMERGENCY_STOP: 'Clôturée par l’arrêt d’urgence',
  CLOSE_REASON_UNKNOWN: 'Motif inconnu',
};

/** FR texts shown when a parameterised code arrives without a usable parameter (C2 spec R8). */
export const reasonFallbackFr: Messages['reasonFallback'] = {
  TELEGRAM_CODE_RATE_LIMITED: 'Telegram limite les demandes de code. Réessaie plus tard.',
};
